import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth, ROLES } from '../../hooks/useAuth';
import { ROUTES, APP_NAME, APP_VERSION, FIREBASE_CONFIG } from '../../constants';
import { formatPrice } from '../../utils';
import { exportData, importData, validateBackupFile } from '../../utils/backupUtils';
import CustomerList from '../customers/CustomerList';
import ProductList from '../productList/ProductList';
import ScheduleReminders from './ScheduleReminders';
import { useLanguage } from '../../i18n/LanguageContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
// import AccountingReport from '../accounting/AccountingReport'; // GEÇİCİ: Hesaplamalar sekmesi devre dışı

import './Dashboard.css';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';

// İstatistik hesaplama sürümü: hesaplama mantığı değişince artırılır, eski cache'ler yok sayılır
// v3: Keşif hatırlatıcıları ve akıllı bölge planlama (ScheduleReminders) eklendi
const STATS_VERSION = 3;

const Dashboard = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const { user, userRole, permissions, isAdmin, isMontajci, isYazilimci, loading } = useAuth();

  // LocalStorage'dan son sekmeyi oku
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('activeTab');
    if (savedTab === 'simulation') return 'dashboard';
    return savedTab || 'dashboard';
  });

  // Rol değiştiğinde veya yüklendiğinde erişim kontrolü
  useEffect(() => {
    if (permissions) {
      // Satışcı dashboard'a erişemez, planlama veya customers'a yönlendir
      if (!permissions.canAccessDashboard && activeTab === 'dashboard') {
        setActiveTab(permissions.canAccessPlanning ? 'planning' : 'customers');
      }
      // Montajcı sadece customers ve planning'e erişebilir
      if (isMontajci && activeTab !== 'customers' && activeTab !== 'planning') {
        setActiveTab(permissions.canAccessPlanning ? 'planning' : 'customers');
      }
      // Kullanıcı yönetimine erişim yoksa ve users sekmesindeyse
      if (!isAdmin && activeTab === 'users') {
        setActiveTab('customers');
      }
      // Kaldırılan Perde Simülasyonu sekmesi
      if (activeTab === 'simulation') {
        setActiveTab('customers');
      }
    }
  }, [permissions, activeTab, isMontajci, isAdmin]);
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0
  });
  const [revenueStats, setRevenueStats] = useState({
    expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
    total: 0,
    remainingTotal: 0, // Kalan ödemeler toplamı
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    thisYear: 0,
    // Ödeme tipine göre günlük gelirler
    todayCash: 0, // Bugün Elden Nakit
    todayIban: 0, // Bugün IBAN
    todayBankCard: 0, // Bugün Banka Kartı
    yesterdayCreditCard: 0
  });
  const [lastYearStats, setLastYearStats] = useState({
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  });
  const [monthlyOrderTrend, setMonthlyOrderTrend] = useState([]);
  const [monthlyCustomerTrend, setMonthlyCustomerTrend] = useState([]);
  const [monthlyEarningsTrend, setMonthlyEarningsTrend] = useState([]);
  const [deliveryReminders, setDeliveryReminders] = useState({
    overdue: [], // Geciken teslimatlar
    today: [], // Bugün teslim edilecek
    tomorrow: [], // Yarın teslim edilecek
    thisWeek: [] // Bu hafta teslim edilecek (2-7 gün)
  });
  const [scheduleCustomers, setScheduleCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  // Randevu kartından müşteriye doğrudan geçiş
  const handleSelectCustomerFromSchedule = useCallback((customerId, customerName) => {
    setCustomerSearchTerm(customerName || '');
    setActiveTab('customers');
  }, []);

  // Keşif tamamlandı / yapıldı durumunu güncelleme
  const handleToggleInspectionCompleted = useCallback(async (customerId, isCompleted) => {
    try {
      const customerRef = doc(db, 'customers', customerId);
      await updateDoc(customerRef, {
        inspectionCompleted: isCompleted,
        updatedAt: new Date()
      });

      // State'i anında güncelle
      setScheduleCustomers(prev => prev.map(c =>
        c.id === customerId ? { ...c, inspectionCompleted: isCompleted } : c
      ));

      // Local cache güncelle
      const cachedData = localStorage.getItem('dashboard_stats_cache');
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (Array.isArray(parsed.scheduleCustomers)) {
            parsed.scheduleCustomers = parsed.scheduleCustomers.map(c =>
              c.id === customerId ? { ...c, inspectionCompleted: isCompleted } : c
            );
            localStorage.setItem('dashboard_stats_cache', JSON.stringify(parsed));
          }
        } catch (e) {}
      }
    } catch (error) {
      console.error('Keşif durumu güncellenirken hata:', error);
      alert('Keşif durumu güncellenirken bir hata oluştu: ' + error.message);
    }
  }, []);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsLastUpdated, setStatsLastUpdated] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);

  //Firma bilgileri state'leri
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    name: '',
    phone: ''
  });
  const [companyError, setCompanyError] = useState(null);
  const [companySuccess, setCompanySuccess] = useState(false);
  const [deleteCompanyModal, setDeleteCompanyModal] = useState(null); // Silinecek firma bilgisi

  //Şifre değiştirme state'leri
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Kullanıcı Yönetimi State'leri
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    password: '',
    displayName: '',
    role: ROLES.SATISCI
  });
  const [userFormError, setUserFormError] = useState(null);
  const [editingUserRole, setEditingUserRole] = useState(null);
  const [deleteUserModal, setDeleteUserModal] = useState(null);

  // Notification Modal State
  const [notificationModal, setNotificationModal] = useState({
    show: false,
    type: 'success', // 'success', 'error', 'warning', 'info'
    title: '',
    message: '',
    details: null
  });

  // Notification Modal göster
  const showNotificationModal = (type, title, message, details = null) => {
    setNotificationModal({
      show: true,
      type,
      title,
      message,
      details
    });
  };

  // Notification Modal kapat
  const closeNotificationModal = () => {
    setNotificationModal({
      show: false,
      type: 'success',
      title: '',
      message: '',
      details: null
    });
  };

  // Tüm istatistikleri çek
  const fetchAllStats = useCallback(async (forceRefresh = false) => {
    try {
      setIsLoadingStats(true);

      // 30 dakikalık cache kontrolü (Firebase Read kotasını korumak için)
      if (!forceRefresh) {
        const cachedData = localStorage.getItem('dashboard_stats_cache');
        const cachedTime = localStorage.getItem('dashboard_stats_time');

        if (cachedData && cachedTime) {
          const ageInMs = Date.now() - parseInt(cachedTime, 10);
          const cacheLimit = 30 * 60 * 1000; // 30 Dakika
          const parsed = JSON.parse(cachedData);
          if (ageInMs < cacheLimit && parsed.statsVersion === STATS_VERSION) {
            setCustomerStats(parsed.customerStats);
            setRevenueStats(parsed.revenueStats);
            setLastYearStats(parsed.lastYearStatsTemp);
            setMonthlyOrderTrend(parsed.monthlyTrendData);
            setMonthlyCustomerTrend(parsed.monthlyCustomerTrendData);
            setMonthlyEarningsTrend(parsed.monthlyEarningsTrendData);
            setDeliveryReminders(parsed.deliveryData || { overdue: [], today: [], tomorrow: [], thisWeek: [] });
            setScheduleCustomers(parsed.scheduleCustomers || []);
            setStatsLastUpdated(new Date(parseInt(cachedTime, 10)));
            setIsLoadingStats(false);
            return;
          }
        }
      }

      // Yazılımcı/Rol kısıtlaması yoksa stats/dashboard birleştirilmiş belgesini (Materialized View) kontrol et
      const isFilteredView = isYazilimci && user?.uid;
      if (!forceRefresh && !isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          const statsDocSnap = await getDoc(statsDocRef);
          if (statsDocSnap.exists()) {
            const data = statsDocSnap.data();
            const updatedAt = data.updatedAt || Date.now();
            const ageInMs = Date.now() - updatedAt;
            const docLimit = 4 * 60 * 60 * 1000; // 4 Saat geçerlilik süresi

            if (ageInMs < docLimit && data.statsVersion === STATS_VERSION) {
              setCustomerStats(data.customerStats);
              setRevenueStats(data.revenueStats);
              setLastYearStats(data.lastYearStatsTemp);
              setMonthlyOrderTrend(data.monthlyTrendData);
              setMonthlyCustomerTrend(data.monthlyCustomerTrendData);
              setMonthlyEarningsTrend(data.monthlyEarningsTrendData);
              setDeliveryReminders(data.deliveryData || { overdue: [], today: [], tomorrow: [], thisWeek: [] });
              setScheduleCustomers(data.scheduleCustomers || []);
              setStatsLastUpdated(new Date(updatedAt));

              // Local cache'i de bu veriyle doldur ki sekme geçişlerinde sıfır veritabanı okuması yapılsın
              const dataToCache = {
                customerStats: data.customerStats,
                revenueStats: data.revenueStats,
                lastYearStatsTemp: data.lastYearStatsTemp,
                monthlyTrendData: data.monthlyTrendData,
                monthlyCustomerTrendData: data.monthlyCustomerTrendData,
                monthlyEarningsTrendData: data.monthlyEarningsTrendData,
                deliveryData: data.deliveryData,
                scheduleCustomers: data.scheduleCustomers || [],
                statsVersion: STATS_VERSION
              };
              localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
              localStorage.setItem('dashboard_stats_time', updatedAt.toString());

              setIsLoadingStats(false);
              return;
            }
          }
        } catch (docErr) {
          console.warn('Birleştirilmiş istatistik belgesi okunamadı, canlı hesaplamaya geçiliyor:', docErr);
        }
      }

      // Müşterileri çek
      const customersSnapshot = await getDocs(collection(db, 'customers'));
      let customers = customersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi oluşturduğu müşterileri filtrele
      if (isYazilimci && user?.uid) {
        customers = customers.filter(c => c.createdBy === user.uid);
      }

      // Siparişleri çek
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      let orders = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Yazılımcı rolü için sadece kendi müşterilerinin siparişlerini filtrele
      if (isYazilimci && user?.uid) {
        const customerIds = customers.map(c => c.id);
        orders = orders.filter(o => customerIds.includes(o.customerId));
      }

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastYear = now.getFullYear() - 1;
      const lastYearYearStart = new Date(lastYear, 0, 1);
      const lastYearYearEnd = new Date(lastYear, 11, 31, 23, 59, 59, 999);
      const lastYearTodayStart = new Date(lastYear, now.getMonth(), now.getDate());
      const lastYearTodayEnd = new Date(lastYear, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearWeekStart = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate() - 6);
      const lastYearWeekEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59, 999);
      const lastYearMonthStart = new Date(lastYear, now.getMonth(), 1);
      const lastYearMonthEnd = new Date(lastYear, now.getMonth() + 1, 0, 23, 59, 59, 999);

      // Müşteri istatistikleri
      const customerStats = {
        total: customers.length,
        active: customers.filter(customer => {
          // Sipariş durumu "Yeni Sipariş" olan müşteriler
          return customer.orderStatus === 'new_order';
        }).length,
        today: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bugün (aynı gün kontrolü)
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const customerDate = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());
          return customerDate.getTime() === today.getTime();
        }).length,
        thisWeek: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Son 7 gün (bugün dahil)
          const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
          sevenDaysAgo.setHours(0, 0, 0, 0);
          return orderDate >= sevenDaysAgo;
        }).length,
        thisMonth: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu ay sipariş veren müşteriler
          return orderDate >= thisMonth;
        }).length,
        thisYear: customers.filter(customer => {
          // Sipariş tarihi yoksa dahil etme
          if (!customer.orderDate) return false;

          let orderDate;
          if (customer.orderDate?.toDate) {
            orderDate = customer.orderDate.toDate();
          } else {
            orderDate = new Date(customer.orderDate);
          }

          // Bu yıl sipariş veren müşteriler (yılın ilk günü)
          const thisYearStart = new Date(now.getFullYear(), 0, 1);
          return orderDate >= thisYearStart;
        }).length
      };

      // Gelir istatistikleri
      const revenueStats = {
        expectedTotal: 0, // Tüm siparişlerin toplamı (beklenti)
        total: 0,
        remainingTotal: 0, // Kalan ödemeler toplamı (Atölye + Firma + Montaj + Teslim Edildi)
        today: 0,
        thisWeek: 0,
        thisMonth: 0,
        thisYear: 0,
        // Ödeme tipine göre günlük gelirler
        todayCash: 0,
        todayIban: 0,
        todayBankCard: 0,
        yesterdayCreditCard: 0
      };

      const lastYearStatsTemp = {
        total: 0,
        today: 0,
        thisWeek: 0,
        thisMonth: 0
      };

      // Firestore Timestamp / {seconds} / ISO string -> Date (geçersizse null)
      const parseAnyDate = (value) => {
        if (!value) return null;
        let parsed;
        if (value?.toDate) {
          parsed = value.toDate();
        } else if (value?.seconds) {
          parsed = new Date(value.seconds * 1000);
        } else {
          parsed = new Date(value);
        }
        return isNaN(parsed.getTime()) ? null : parsed;
      };

      // Gelir istatistikleri ÖDEME TARİHİNE göre hesaplanır
      const revenueTodayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const revenueTomorrowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const revenueWeekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6); // Son 7 gün (bugün dahil)
      const revenueYearStart = new Date(now.getFullYear(), 0, 1);
      const monthlyEarningsCounts = Array(12).fill(0);

      const addRevenueByDate = (amount, date) => {
        if (!amount || !date) return;
        if (date >= revenueTomorrowStart) return; // Gelecek tarihli kayıtlar dönemsel istatistiklere girmez
        if (date >= revenueTodayStart) revenueStats.today += amount;
        if (date >= revenueWeekStart) revenueStats.thisWeek += amount;
        if (date >= thisMonth) revenueStats.thisMonth += amount;
        if (date >= revenueYearStart) {
          revenueStats.thisYear += amount;
          monthlyEarningsCounts[date.getMonth()] += amount; // Aylık Kazanç Trendi
        }
      };

      // Her müşteri için ödenen tutarları kontrol et (gerçek kazanç)
      customers.forEach(customer => {
        // Müşterinin ödenen tutarını al (gerçek kazanç)
        const paidAmount = parseFloat(customer.paidAmount || 0);

        // Toplam Gelir Beklentisi - Tüm siparişler için beklenen gelir
        // Müşterinin siparişlerini bul
        const customerOrders = orders.filter(order => order.customerId === customer.id);

        if (customerOrders.length > 0) {
          // Toplam tutar
          const totalAmount = customerOrders.reduce((sum, order) => {
            return sum + parseFloat(order.totalPrice || 0);
          }, 0);

          // İndirimli tutar
          const discountedTotalAmount = customerOrders.reduce((sum, order) => {
            if (order.discountedPrice && parseFloat(order.discount) > 0) {
              return sum + parseFloat(order.discountedPrice || 0);
            } else {
              return sum + parseFloat(order.totalPrice || 0);
            }
          }, 0);

          // Ahşap Jaluzi toplamı
          const ahsapJaluziTotal = customerOrders.reduce((sum, order) => {
            if (order.productType === 'ahsapJaluzi') {
              return sum + parseFloat(order.totalPrice || 0);
            }
            return sum;
          }, 0);

          // Vadeli hesaplama
          const vadeliYuzde = parseFloat(customer.vadeliYuzde) || 0;
          const vadeliTutar = totalAmount - ahsapJaluziTotal;
          const nakitOdeme = ahsapJaluziTotal;

          // Toplam ödeme hesaplama (vadeli varsa)
          const toplamOdeme = vadeliYuzde > 0
            ? (vadeliTutar + (vadeliTutar * vadeliYuzde / 100)) + nakitOdeme
            : null;

          // Beklenen gelir hesaplama: Vadeli varsa toplam ödeme, yoksa indirimli tutar, o da yoksa toplam tutar
          let customerTotalRevenue = 0;
          if (toplamOdeme !== null) {
            // Vadeli varsa toplam ödeme
            customerTotalRevenue = toplamOdeme;
          } else if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) {
            // İndirimli tutar varsa
            customerTotalRevenue = discountedTotalAmount;
          } else {
            // Sadece toplam tutar
            customerTotalRevenue = totalAmount;
          }

          revenueStats.expectedTotal += customerTotalRevenue;
        }

        // Kalan Ödemeler - Sadece Atölye + Firma + Montaj + Teslim Edildi durumundaki müşterilerin kalan ödemeleri
        if (['atolye', 'firma', 'montaj', 'delivered'].includes(customer.orderStatus)) {
          const remainingAmount = parseFloat(customer.remainingAmount || 0);
          revenueStats.remainingTotal += remainingAmount;
        }

        const paymentHistory = Array.isArray(customer.paymentHistory) ? customer.paymentHistory : [];
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let historyTotal = 0;

        paymentHistory.forEach(payment => {
          const amount = parseFloat(payment.amount) || 0;
          historyTotal += amount;

          const paymentDate = parseAnyDate(payment.date);
          if (!paymentDate) return;

          // Bugün / Bu Hafta / Bu Ay / Bu Yıl + Aylık Kazanç Trendi
          addRevenueByDate(amount, paymentDate);

          const paymentDateOnly = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), paymentDate.getDate());

          // Bugün Elden Nakit
          if (payment.type === 'cash' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayCash += amount;
          }

          // Bugün IBAN
          if (payment.type === 'iban' && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayIban += amount;
          }

          // Bugün Banka Kartı
          if ((payment.type === 'bank_card' || payment.type === 'credit_card') && paymentDateOnly.getTime() === today.getTime()) {
            revenueStats.todayBankCard += amount;
            revenueStats.yesterdayCreditCard += amount;
          }
        });

        // Eski kayıtlar: ödeme geçmişine yansımamış ödenen tutar (tarihi bilinmiyor) → sipariş tarihine göre sayılır
        const unrecordedPaid = Math.max(0, paidAmount - historyTotal);
        if (unrecordedPaid > 0.01) {
          addRevenueByDate(unrecordedPaid, parseAnyDate(customer.orderDate));
        }

        // Toplam Gelir - Alınan tüm ödemeler (gerçek kazanç)
        revenueStats.total += historyTotal + unrecordedPaid;

        // Sipariş tarihi yoksa geçen yıl karşılaştırmalarına dahil etme
        if (!customer.orderDate) return;

        // Müşterinin sipariş tarihini al
        let customerDate;
        if (customer.orderDate?.toDate) {
          customerDate = customer.orderDate.toDate();
        } else {
          customerDate = new Date(customer.orderDate);
        }

        // Geçen yıl karşılaştırmaları
        if (customerDate >= lastYearYearStart && customerDate <= lastYearYearEnd) {
          lastYearStatsTemp.total += 1;
        }

        if (customerDate >= lastYearTodayStart && customerDate <= lastYearTodayEnd) {
          lastYearStatsTemp.today += 1;
        }

        if (customerDate >= lastYearWeekStart && customerDate <= lastYearWeekEnd) {
          lastYearStatsTemp.thisWeek += 1;
        }

        if (customerDate >= lastYearMonthStart && customerDate <= lastYearMonthEnd) {
          lastYearStatsTemp.thisMonth += 1;
        }
      });

      // Aylık sipariş trendi (mevcut yıl)
      const monthLabels = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
      const currentYear = now.getFullYear();
      const monthlyTrendCounts = Array(12).fill(0);
      const monthlyCustomerCounts = Array(12).fill(0);

      // Sipariş tarihlerini müşteriden okur (orders içinde orderDate yok)
      const customerOrderDateMap = new Map();
      customers.forEach(customer => {
        const dateToUse = customer.orderDate;
        let parsed = null;
        if (dateToUse?.toDate) {
          parsed = dateToUse.toDate();
        } else if (dateToUse) {
          parsed = new Date(dateToUse);
        }
        if (parsed && !isNaN(parsed.getTime())) {
          customerOrderDateMap.set(customer.id, parsed);
        }
      });

      orders.forEach(order => {
        // Sipariş Tarihi müşteri kaydından alınır; yoksa ekleme
        const orderDate = customerOrderDateMap.get(order.customerId);
        if (!orderDate) return;
        if (orderDate.getFullYear() === currentYear) {
          let orderQuantity = 0;
          if (order.productType === 'plise') {
            // Plise ise items içindeki her bir kalemin adetini topla
            orderQuantity = (order.pliseItems || []).reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
          } else {
            // Diğerleri için quantity alanını topla
            orderQuantity = parseInt(order.quantity) || 0;
          }
          monthlyTrendCounts[orderDate.getMonth()] += orderQuantity;
        }
      });

      customers.forEach(customer => {
        // Müşteri trendi de sipariş tarihine göre; tarih yoksa ekleme
        const dateToUse = customer.orderDate;
        let customerDate = null;

        if (dateToUse?.toDate) {
          customerDate = dateToUse.toDate();
        } else if (dateToUse) {
          customerDate = new Date(dateToUse);
        }

        if (!customerDate || isNaN(customerDate.getTime())) return;

        if (customerDate.getFullYear() === currentYear) {
          monthlyCustomerCounts[customerDate.getMonth()] += 1;
        }
      });

      const monthlyTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyTrendCounts[index]
      }));

      const monthlyCustomerTrendData = monthLabels.map((label, index) => ({
        month: label,
        count: monthlyCustomerCounts[index]
      }));

      // Aylık Kazanç Trendi: yukarıda ödeme tarihine göre (addRevenueByDate) hesaplandı
      const monthlyEarningsTrendData = monthLabels.map((label, index) => ({
        month: label,
        earnings: monthlyEarningsCounts[index]
      }));

      // Teslimat Hatırlatıcı Hesaplaması
      const todayDelivery = new Date();
      todayDelivery.setHours(0, 0, 0, 0);

      const tomorrow = new Date(todayDelivery);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const weekEnd = new Date(todayDelivery);
      weekEnd.setDate(weekEnd.getDate() + 7);

      const deliveryData = {
        overdue: [],
        today: [],
        tomorrow: [],
        thisWeek: []
      };

      // Teslim edilmemiş müşterileri filtrele (delivered hariç)
      const pendingCustomers = customers.filter(c =>
        c.orderStatus && c.orderStatus !== 'delivered' && c.deliveryDate
      );

      pendingCustomers.forEach(customer => {
        let deliveryDate;
        if (customer.deliveryDate?.toDate) {
          deliveryDate = customer.deliveryDate.toDate();
        } else {
          deliveryDate = new Date(customer.deliveryDate);
        }
        deliveryDate.setHours(0, 0, 0, 0);

        const customerInfo = {
          id: customer.id,
          name: customer.name,
          phone: customer.phone1,
          address: customer.address,
          deliveryDate: customer.deliveryDate,
          orderStatus: customer.orderStatus,
          daysLeft: Math.ceil((deliveryDate - todayDelivery) / (1000 * 60 * 60 * 24))
        };

        if (deliveryDate < todayDelivery) {
          // Gecikmiş
          customerInfo.daysOverdue = Math.abs(customerInfo.daysLeft);
          deliveryData.overdue.push(customerInfo);
        } else if (deliveryDate.getTime() === todayDelivery.getTime()) {
          // Bugün
          deliveryData.today.push(customerInfo);
        } else if (deliveryDate.getTime() === tomorrow.getTime()) {
          // Yarın
          deliveryData.tomorrow.push(customerInfo);
        } else if (deliveryDate < weekEnd) {
          // Bu hafta (2-7 gün)
          deliveryData.thisWeek.push(customerInfo);
        }
      });

      // Teslimatları en yakın tarihten ileri tarihe göre sırala
      deliveryData.overdue.sort((a, b) => b.daysOverdue - a.daysOverdue); // En çok geciken en üstte
      deliveryData.today.sort((a, b) => a.name.localeCompare(b.name)); // Bugünkileri isim sırasına göre
      deliveryData.tomorrow.sort((a, b) => a.name.localeCompare(b.name)); // Yarınkileri isim sırasına göre
      deliveryData.thisWeek.sort((a, b) => a.daysLeft - b.daysLeft); // Bu haftayı en yakın tarihten uzağa

      // Aktif randevusu olan müşterileri ScheduleReminders için hazırla
      const activeScheduleCustomers = customers
        .filter(c => c && c.orderStatus !== 'delivered' && (c.deliveryDate || c.inspectionDate))
        .map(c => ({
          id: c.id,
          name: c.name || '',
          phone1: c.phone1 || '',
          phone2: c.phone2 || '',
          address: c.address || '',
          city: c.city || '',
          postalCode: c.postalCode || '',
          orderStatus: c.orderStatus || 'new_order',
          deliveryDate: c.deliveryDate || '',
          inspectionDate: c.inspectionDate || '',
          inspectionCompleted: !!(c.inspectionCompleted || c.isInspectionDone)
        }));

      setCustomerStats(customerStats);
      setRevenueStats(revenueStats);
      setLastYearStats(lastYearStatsTemp);
      setMonthlyOrderTrend(monthlyTrendData);
      setMonthlyCustomerTrend(monthlyCustomerTrendData);
      setMonthlyEarningsTrend(monthlyEarningsTrendData);
      setDeliveryReminders(deliveryData);
      setScheduleCustomers(activeScheduleCustomers);

      // Cache'e kaydet (Firebase Read kotasını korumak için)
      const dataToCache = {
        customerStats,
        revenueStats,
        lastYearStatsTemp,
        monthlyTrendData,
        monthlyCustomerTrendData,
        monthlyEarningsTrendData,
        deliveryData,
        scheduleCustomers: activeScheduleCustomers,
        statsVersion: STATS_VERSION
      };
      const updateTime = new Date();
      localStorage.setItem('dashboard_stats_cache', JSON.stringify(dataToCache));
      localStorage.setItem('dashboard_stats_time', updateTime.getTime().toString());
      setStatsLastUpdated(updateTime);

      // Yazılımcı filtresi yoksa bu güncel veriyi tek bir write ile stats/dashboard belgesine de yaz (Materialized View Güncelleme)
      if (!isFilteredView) {
        try {
          const statsDocRef = doc(db, 'stats', 'dashboard');
          await setDoc(statsDocRef, {
            ...dataToCache,
            updatedAt: updateTime.getTime()
          });
        } catch (writeErr) {
          console.error('Birleştirilmiş istatistik belgesi güncellenemedi:', writeErr);
        }
      }
    } catch (error) {
      console.error('İstatistikler yüklenirken hata:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [isYazilimci, user?.uid]);

  const handleCustomerChange = useCallback(() => {
    localStorage.removeItem('dashboard_stats_cache');
    localStorage.removeItem('dashboard_stats_time');
    setStatsLastUpdated(null);
  }, []);

  // İstatistikleri yükle (Ana Sayfa veya Planlama sekmesinde verileri yükle)
  useEffect(() => {
    if (activeTab === 'dashboard' || activeTab === 'planning') {
      fetchAllStats(false);
    }
  }, [fetchAllStats, activeTab]);

  // Aktif sekme değiştiğinde localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);



  // Firma bilgilerini Firebase'den çek
  const fetchCompanies = useCallback(async () => {
    try {
      setIsLoadingCompanies(true);
      const companiesSnapshot = await getDocs(collection(db, 'companies'));
      const companiesData = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // İsme göre sırala
      companiesData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firma bilgileri yüklenirken hata:', error);
      setCompanyError('Firma bilgileri yüklenirken bir hata oluştu.');
    } finally {
      setIsLoadingCompanies(false);
    }
  }, []);

  // Firma bilgileri modalı açıldığında verileri çek
  useEffect(() => {
    if (showCompanyInfo) {
      fetchCompanies();
    }
  }, [showCompanyInfo, fetchCompanies]);

  // Firma ekle veya güncelle
  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanyError(null);
    setCompanySuccess(false);

    // Validasyonlar
    if (!companyForm.name || !companyForm.phone) {
      setCompanyError('Lütfen firma adı ve telefon numarasını girin.');
      return;
    }

    // Telefon numarası validasyonu (sadece rakam ve + işareti)
    const phoneRegex = /^[+]?[0-9]{10,15}$/;
    if (!phoneRegex.test(companyForm.phone.replace(/\s/g, ''))) {
      setCompanyError('Geçerli bir telefon numarası girin (örn: 905321234567 veya +905321234567)');
      return;
    }

    try {
      const phoneNumber = companyForm.phone.replace(/\s/g, ''); // Boşlukları temizle

      if (editingCompany) {
        // Güncelleme
        await updateDoc(doc(db, 'companies', editingCompany.id), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          updatedAt: new Date()
        });
        setCompanySuccess('Firma bilgileri başarıyla güncellendi!');
      } else {
        // Yeni ekleme
        // Aynı isimde firma var mı kontrol et
        const existingCompany = companies.find(c =>
          c.name.toLowerCase().trim() === companyForm.name.toLowerCase().trim()
        );
        if (existingCompany) {
          setCompanyError('Bu isimde bir firma zaten mevcut.');
          return;
        }

        await addDoc(collection(db, 'companies'), {
          name: companyForm.name.trim(),
          phone: phoneNumber,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: user?.uid || null
        });
        setCompanySuccess('Firma başarıyla eklendi!');
      }

      // Formu temizle
      setCompanyForm({ name: '', phone: '' });
      setEditingCompany(null);

      // Listeyi yeniden yükle
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma kaydedilirken hata:', error);
      let errorMessage = 'Firma kaydedilirken bir hata oluştu.';
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        errorMessage = 'Firma ekleme yetkiniz bulunmamaktadır. Lütfen yöneticinizle iletişime geçin.';
      } else if (error.message) {
        errorMessage = `Firma kaydedilirken bir hata oluştu: ${error.message}`;
      }
      setCompanyError(errorMessage);
    }
  };

  // Firma düzenle
  const handleEditCompany = (company) => {
    setEditingCompany(company);
    setCompanyForm({
      name: company.name || '',
      phone: company.phone || ''
    });
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // Firma silme modal'ını aç
  const handleDeleteCompanyClick = (company) => {
    setDeleteCompanyModal(company);
  };

  // Firma silme modal'ını kapat
  const handleCloseDeleteCompanyModal = () => {
    setDeleteCompanyModal(null);
  };

  // Firma sil
  const handleDeleteCompany = async () => {
    if (!deleteCompanyModal) return;

    try {
      await deleteDoc(doc(db, 'companies', deleteCompanyModal.id));
      setCompanySuccess('Firma başarıyla silindi!');
      setDeleteCompanyModal(null);
      await fetchCompanies();

      // 3 saniye sonra başarı mesajını kaldır
      setTimeout(() => {
        setCompanySuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Firma silinirken hata:', error);
      setCompanyError('Firma silinirken bir hata oluştu: ' + error.message);
      setDeleteCompanyModal(null);
    }
  };

  // Firma formunu iptal et
  const handleCancelCompany = () => {
    setCompanyForm({ name: '', phone: '' });
    setEditingCompany(null);
    setCompanyError(null);
    setCompanySuccess(false);
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI ========

  // Kullanıcıları getir
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    setIsLoadingUsers(true);
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(user => !user.disabled); // Disabled kullanıcıları filtrele
      setUsers(usersData);
    } catch (error) {
      console.error('Kullanıcılar alınırken hata:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [isAdmin]);

  // Kullanıcıları yükle (admin ise)
  useEffect(() => {
    if (isAdmin && activeTab === 'users') {
      fetchUsers();
    }
  }, [isAdmin, activeTab, fetchUsers]);

  // Yeni kullanıcı oluştur
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setUserFormError(null);

    if (!newUserForm.email || !newUserForm.password || !newUserForm.displayName) {
      setUserFormError('Tüm alanları doldurun');
      return;
    }

    if (newUserForm.password.length < 6) {
      setUserFormError('Şifre en az 6 karakter olmalıdır');
      return;
    }

    try {
      // Firebase Auth'da kullanıcı oluştur - Secondary auth instance kullanıyoruz
      // Ana kullanıcının oturumunu kapatmadan yeni kullanıcı oluşturmak için
      const { initializeApp, getApps, deleteApp } = await import('firebase/app');
      const { getAuth, createUserWithEmailAndPassword: createUser } = await import('firebase/auth');

      // Mevcut Secondary app varsa sil
      const apps = getApps();
      const existingSecondary = apps.find(app => app.name === 'Secondary');
      if (existingSecondary) {
        await deleteApp(existingSecondary);
      }

      // Geçici bir Firebase app instance oluştur (constants'dan config al)
      const secondaryApp = initializeApp(FIREBASE_CONFIG, 'Secondary');
      const secondaryAuth = getAuth(secondaryApp);

      try {
        const userCredential = await createUser(secondaryAuth, newUserForm.email, newUserForm.password);
        const newUser = userCredential.user;

        // Firestore'da kullanıcı kaydı oluştur
        await setDoc(doc(db, 'users', newUser.uid), {
          email: newUserForm.email,
          displayName: newUserForm.displayName,
          role: newUserForm.role,
          createdAt: new Date(),
          createdBy: user.uid
        });

        // Secondary app'i temizle
        await deleteApp(secondaryApp);

        // Formu sıfırla ve modalı kapat
        setNewUserForm({
          email: '',
          password: '',
          displayName: '',
          role: ROLES.SATISCI
        });
        setShowAddUserModal(false);

        // Kullanıcı listesini yenile
        fetchUsers();

        showNotificationModal('success', 'Başarılı', `${newUserForm.displayName} kullanıcısı başarıyla oluşturuldu.`);
      } catch (error) {
        await deleteApp(secondaryApp);
        throw error;
      }
    } catch (error) {
      console.error('Kullanıcı oluşturulurken hata:', error);
      if (error.code === 'auth/email-already-in-use') {
        setUserFormError('Bu e-posta adresi zaten kullanılıyor');
      } else if (error.code === 'auth/invalid-email') {
        setUserFormError('Geçersiz e-posta adresi');
      } else if (error.code === 'auth/weak-password') {
        setUserFormError('Şifre çok zayıf');
      } else {
        setUserFormError('Kullanıcı oluşturulurken bir hata oluştu');
      }
    }
  };

  // Kullanıcı rolünü güncelle
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await updateDoc(doc(db, 'users', userId), {
        role: newRole,
        updatedAt: new Date(),
        updatedBy: user.uid
      });

      // Local state güncelle
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, role: newRole } : u
      ));
      setEditingUserRole(null);

      showNotificationModal('success', 'Başarılı', 'Kullanıcı rolü güncellendi');
    } catch (error) {
      console.error('Rol güncellenirken hata:', error);
      showNotificationModal('error', 'Hata', 'Rol güncellenirken bir hata oluştu');
    }
  };

  // Kullanıcı sil - Kullanıcıyı disabled olarak işaretle (Firestore'dan silme, sadece devre dışı bırak)
  const handleDeleteUser = async (userId) => {
    try {
      // Önce kullanıcı bilgilerini al
      const userToDelete = users.find(u => u.id === userId);
      if (!userToDelete) {
        showNotificationModal('error', 'Hata', 'Kullanıcı bulunamadı');
        return;
      }

      // Firebase Auth'dan kullanıcıyı silmek için Cloud Functions endpoint'ini dene
      try {
        const functions = await import('firebase/functions');
        const { getFunctions, httpsCallable } = functions;
        const funcs = getFunctions();
        const deleteUserFunction = httpsCallable(funcs, 'deleteUser');

        // Cloud Functions ile Firebase Auth'dan da sil
        await deleteUserFunction({ userId: userId, email: userToDelete.email });

        // Başarılı olursa Firestore'dan da sil
        await deleteDoc(doc(db, 'users', userId));
      } catch (functionsError) {
        // Cloud Functions yoksa veya hata varsa, sadece disabled olarak işaretle
        // Firestore'dan SİLME, sadece disabled yap
        await updateDoc(doc(db, 'users', userId), {
          disabled: true,
          disabledAt: new Date(),
          disabledBy: user.uid
        });
      }

      // Kullanıcı listesini yeniden yükle
      await fetchUsers();

      setDeleteUserModal(null);
      showNotificationModal('success', 'Başarılı', 'Kullanıcı başarıyla devre dışı bırakıldı');
    } catch (error) {
      console.error('Kullanıcı silinirken hata:', error);
      showNotificationModal('error', 'Hata', 'Kullanıcı silinirken bir hata oluştu: ' + error.message);
    }
  };

  // Rol adını Türkçe olarak döndür
  const getRoleName = (role) => {
    const roleNames = {
      [ROLES.ADMIN]: t('userManagement.roles.admin', 'Admin'),
      [ROLES.SATISCI]: t('userManagement.roles.satisci', 'Satışcı'),
      [ROLES.MONTAJCI]: t('userManagement.roles.montajci', 'Montajcı'),
      [ROLES.YAZILIMCI]: t('userManagement.roles.yazilimci', 'Yazılımcı')
    };
    return roleNames[role] || role;
  };

  // Rol badge rengini döndür
  const getRoleBadgeClass = (role) => {
    const classes = {
      [ROLES.ADMIN]: 'role-badge-admin',
      [ROLES.SATISCI]: 'role-badge-satisci',
      [ROLES.MONTAJCI]: 'role-badge-montajci',
      [ROLES.YAZILIMCI]: 'role-badge-yazilimci'
    };
    return classes[role] || '';
  };

  // ======== KULLANICI YÖNETİMİ FONKSİYONLARI SONU ========

  const handleLogout = async () => {
    try {
      // Firebase'den çıkış yap
      await signOut(auth);
      // Firebase otomatik olarak oturumu temizler
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Çıkış hatası:', error);
      // Hata olsa bile ana sayfaya yönlendir
      navigate(ROUTES.LOGIN);
    }
  };


  // Backup fonksiyonu
  const handleExport = async () => {
    try {
      setIsExporting(true);
      const result = await exportData(userRole, user?.uid);
      showNotificationModal(
        'success',
        'Yedekleme Başarılı',
        'Verileriniz başarıyla yedeklendi.',
        {
          customers: result.customers,
          orders: result.orders,
          pieces: result.pieces,
          companies: result.companies
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yedekleme Hatası', error.message);
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Import modal'ını aç
  const handleImportClick = () => {
    setShowImportModal(true);
    setImportFile(null);
  };

  // Dosya seçimi
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        validateBackupFile(file);
        setImportFile(file);
      } catch (error) {
        showNotificationModal('error', 'Dosya Hatası', error.message);
        event.target.value = '';
      }
    }
  };

  // Import işlemi
  const handleImport = async () => {
    if (!importFile) {
      showNotificationModal('warning', 'Dosya Seçilmedi', 'Lütfen bir yedek dosyası seçin.');
      return;
    }

    try {
      setIsImporting(true);
      const result = await importData(importFile);

      // Modal'ı kapat
      setShowImportModal(false);
      setImportFile(null);

      // İstatistikleri yeniden yükle
      fetchAllStats();

      // Başarı modal'ını göster
      showNotificationModal(
        'success',
        'Yükleme Başarılı',
        'Yedek dosyası başarıyla yüklendi.',
        {
          backupCustomers: result.totalCustomers,
          backupOrders: result.totalOrders,
          backupCompanies: result.totalCompanies,
          addedCustomers: result.addedCustomers,
          addedOrders: result.addedOrders,
          addedCompanies: result.addedCompanies
        }
      );
    } catch (error) {
      showNotificationModal('error', 'Yükleme Hatası', error.message);
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  // Modal'ı kapat
  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
  };

  //Şifre değiştirme fonksiyonu
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validasyonlar
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Lütfen tüm alanları doldurun.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Yeni şifre ve şifre onayı eşleşmiyor.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('Yeni şifre eski şifre ile aynı olamaz.');
      return;
    }

    try {
      setIsChangingPassword(true);

      //Mevcut şifreyi doğrula
      const user = auth.currentUser;
      if (!user || !user.email) {
        setPasswordError("Kullanıcı bilgisi bulunamadı.");
        setIsChangingPassword(false);
        return;
      }

      // Mevcut şifreyi doğrula (ayrı try-catch ile)
      try {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      } catch (authError) {
        console.error('Şifre doğrulama hatası:', authError);

        // Mevcut şifre ile ilgili hatalar
        if (authError.code === 'auth/wrong-password' ||
          authError.code === 'auth/invalid-credential' ||
          authError.code === 'auth/invalid-email' ||
          authError.message?.includes('password') ||
          authError.message?.includes('credential')) {
          setPasswordError('Mevcut şifre hatalı. Lütfen doğru şifreyi girin.');
        } else if (authError.code === 'auth/user-mismatch') {
          setPasswordError('Kullanıcı bilgisi eşleşmiyor. Lütfen tekrar giriş yapın.');
        } else if (authError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Mevcut şifre doğrulanamadı. Lütfen şifrenizi kontrol edin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Şifreyi güncelle (ayrı try-catch ile)
      try {
        await updatePassword(user, newPassword);
      } catch (updateError) {
        console.error('Şifre güncelleme hatası:', updateError);

        if (updateError.code === 'auth/weak-password') {
          setPasswordError('Yeni şifre çok zayıf. Daha güçlü bir şifre seçin (en az 6 karakter, büyük/küçük harf, rakam önerilir).');
        } else if (updateError.code === 'auth/requires-recent-login') {
          setPasswordError('Güvenlik nedeniyle lütfen tekrar giriş yapın ve şifre değiştirmeyi tekrar deneyin.');
        } else {
          setPasswordError('Şifre güncellenirken bir hata oluştu. Lütfen tekrar deneyin.');
        }
        setIsChangingPassword(false);
        return;
      }

      // Başarılı
      setPasswordSuccess('Şifreniz başarıyla değiştirildi!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // 3 saniye sonra formu kapat
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 3000);
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setPasswordError('Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Kullanıcı Yönetimi Render
  const renderUserManagement = () => {
    return (
      <div className="user-management-content">
        <div className="section-header">
          <h2><i className="fas fa-users-cog"></i> {t('userManagement.title', 'Kullanıcı Yönetimi')}</h2>
          <button
            className="btn-add-user"
            onClick={() => setShowAddUserModal(true)}
          >
            <i className="fas fa-user-plus"></i>
            {t('userManagement.addUser', 'Yeni Kullanıcı')}
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>{language === 'nl' ? 'Gebruikers laden...' : 'Kullanıcılar yükleniyor...'}</span>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>{t('userManagement.name', 'Kullanıcı Adı')}</th>
                  <th>{t('userManagement.email', 'E-posta')}</th>
                  <th>{t('userManagement.role', 'Rol')}</th>
                  <th>{language === 'nl' ? 'Aanmaakdatum' : 'Oluşturulma Tarihi'}</th>
                  <th>{t('userManagement.actions', 'İşlemler')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>{u.displayName || '-'}</td>
                    <td>{u.email}</td>
                    <td>
                      {editingUserRole === u.id ? (
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdateUserRole(u.id, e.target.value)}
                          onBlur={() => setEditingUserRole(null)}
                          autoFocus
                        >
                          <option value={ROLES.ADMIN}>{t('userManagement.roles.admin', 'Admin')}</option>
                          <option value={ROLES.SATISCI}>{t('userManagement.roles.satisci', 'Satışcı')}</option>
                          <option value={ROLES.MONTAJCI}>{t('userManagement.roles.montajci', 'Montajcı')}</option>
                          <option value={ROLES.YAZILIMCI}>{t('userManagement.roles.yazilimci', 'Yazılımcı')}</option>
                        </select>
                      ) : (
                        <span
                          className={`role-badge ${getRoleBadgeClass(u.role)}`}
                          onClick={() => u.id !== user?.uid && setEditingUserRole(u.id)}
                          style={{ cursor: u.id !== user?.uid ? 'pointer' : 'default' }}
                          title={u.id !== user?.uid ? (language === 'nl' ? 'Klik om rol te wijzigen' : 'Rolü değiştirmek için tıklayın') : (language === 'nl' ? 'U kunt uw eigen rol niet wijzigen' : 'Kendi rolünüzü değiştiremezsiniz')}
                        >
                          {getRoleName(u.role)}
                        </span>
                      )}
                    </td>
                    <td>
                      {u.createdAt?.toDate?.()
                        ? new Date(u.createdAt.toDate()).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'tr-TR')
                        : '-'}
                    </td>
                    <td>
                      {u.id !== user?.uid ? (
                        <button
                          className="btn-delete-user"
                          onClick={() => setDeleteUserModal(u)}
                          title={language === 'nl' ? 'Gebruiker verwijderen' : 'Kullanıcıyı Sil'}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      ) : (
                        <span className="current-user-badge">{language === 'nl' ? 'U' : 'Siz'}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {users.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">
                      {language === 'nl' ? 'Er zijn nog geen gebruikers gevonden.' : 'Henüz kullanıcı bulunmamaktadır.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="role-info-section">
          <h3><i className="fas fa-info-circle"></i> {t('userManagement.rolePermissions', 'Rol Yetkileri')}</h3>
          <div className="role-info-grid">
            <div className="role-info-card">
              <span className="role-badge role-badge-admin">{t('userManagement.roles.admin', 'Admin')}</span>
              <p>{t('userManagement.roleDescriptions.admin', 'Tüm sisteme tam erişim. Kullanıcı oluşturma ve silme yetkisi.')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-satisci">{t('userManagement.roles.satisci', 'Satışçı')}</span>
              <p>{t('userManagement.roleDescriptions.satisci', "Müşteri ve sipariş ekleme, düzenleme, silme. Dashboard'a erişim yok.")}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-montajci">{t('userManagement.roles.montajci', 'Montajcı')}</span>
              <p>{t('userManagement.roleDescriptions.montajci', 'Sadece montaj durumundaki siparişleri görme ve teslim edildi olarak işaretleme.')}</p>
            </div>
            <div className="role-info-card">
              <span className="role-badge role-badge-yazilimci">{t('userManagement.roles.yazilimci', 'Yazılımcı')}</span>
              <p>{t('userManagement.roleDescriptions.yazilimci', 'Tüm özelliklere erişim, ancak sadece kendi oluşturduğu verileri görme.')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'planning':
        return (
          <div className="planning-page-wrapper">
            <ScheduleReminders
              customers={scheduleCustomers}
              deliveryReminders={deliveryReminders}
              onSelectCustomer={handleSelectCustomerFromSchedule}
              onToggleInspection={handleToggleInspectionCompleted}
              isStandalonePage={true}
            />
          </div>
        );
      case 'customers':
        return (
          <CustomerList
            onCustomerChange={handleCustomerChange}
            userRole={userRole}
            userId={user?.uid}
            initialSearchTerm={customerSearchTerm}
            onClearSearch={() => setCustomerSearchTerm('')}
          />
        );
      case 'products':
        if (isMontajci) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <ProductList />;
      case 'users':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return renderUserManagement();
      /* GEÇİCİ DEVRE DIŞI - Hesaplamalar sekmesi kaldırıldı
      case 'accounting':
        if (!isAdmin) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
            </div>
          );
        }
        return <AccountingReport />;
      */
      case 'dashboard':
      default:
        // Satışcı dashboard'a erişemez
        if (!permissions?.canAccessDashboard) {
          return (
            <div className="access-denied-card">
              <i className="fas fa-lock"></i>
              <h2>Erişim Reddedildi</h2>
              <p>Dashboard'a erişim yetkiniz bulunmamaktadır.</p>
              <p>Müşteri Listesi sekmesinden işlemlerinizi yapabilirsiniz.</p>
            </div>
          );
        }
        return (
          <div className="dashboard-content">
            <div className="welcome-card">
              <h2>{language === 'nl' ? 'Welkom!' : 'Hoş Geldiniz!'}</h2>
              <p>{language === 'nl' ? 'U bent succesvol ingelogd op het klant- en bestelbeheersysteem.' : 'Müşteri listesi yönetim paneline başarıyla giriş yaptınız.'}</p>
              <p>{language === 'nl' ? 'Hier kunt u al uw klanten, bestellingen en planning beheren.' : 'Burada müşteri listesi işlemlerinizi gerçekleştirebilirsiniz.'}</p>
              {user?.email && (
                <div className="user-info">
                  <p><strong>{language === 'nl' ? 'Ingelogd als:' : 'Giriş Yapan:'}</strong> {user.email}</p>
                  {userRole && (
                    <p><strong>{t('userManagement.role', 'Rol')}:</strong> <span className={`role-badge ${getRoleBadgeClass(userRole)}`}>{getRoleName(userRole)}</span></p>
                  )}
                </div>
              )}
            </div>

            <div className="cache-info-banner">
              <div className="cache-info-text">
                <i className="fas fa-history"></i>
                <span>
                  {language === 'nl' ? (
                    <>Statistieken laatst bijgewerkt om <strong>{statsLastUpdated ? statsLastUpdated.toLocaleTimeString('nl-NL') : '...'}</strong>.</>
                  ) : (
                    <>İstatistikler en son <strong>{statsLastUpdated ? statsLastUpdated.toLocaleTimeString('tr-TR') : '...'}</strong> saatinde güncellendi.</>
                  )}
                </span>
              </div>
              <button
                className="refresh-stats-btn"
                onClick={() => fetchAllStats(true)}
                disabled={isLoadingStats}
              >
                {isLoadingStats ? (
                  <><i className="fas fa-spinner fa-spin"></i> {language === 'nl' ? 'Bijwerken...' : 'Güncelleniyor...'}</>
                ) : (
                  <><i className="fas fa-sync-alt"></i> {language === 'nl' ? 'Gegevens Vernieuwen' : 'Verileri Yenile'}</>
                )}
              </button>
            </div>

            {/* Müşteri İstatistikleri */}
            <div className="stats-section">
              <h3>{t('dashboard.customerStats', 'Müşteri İstatistikleri')}</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.totalCustomers', 'Toplam Müşteri')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.total}
                    </div>
                    <small>{language === 'nl' ? 'Alle geregistreerde klanten' : 'Tüm kayıtlı müşteriler'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-user-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.activeCustomers', 'Aktif Müşteri')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.active}
                    </div>
                    <small>{language === 'nl' ? 'In bestelstatus' : 'Yeni sipariş durumunda'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.today', 'Bugün')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.today}
                    </div>
                    <small>{language === 'nl' ? 'Nieuwe bestelling vandaag' : 'Bugün sipariş oluşturan'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisWeek', 'Bu Hafta')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisWeek}
                    </div>
                    <small>{language === 'nl' ? 'Laatste 7 dagen' : 'Son 7 günde sipariş oluşturan'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisMonth', 'Bu Ay')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisMonth}
                    </div>
                    <small>{language === 'nl' ? 'Deze maand' : 'Bu ay sipariş oluşturan'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-chart-line"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisYear', 'Bu Yıl')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : customerStats.thisYear}
                    </div>
                    <small>{language === 'nl' ? 'Dit jaar' : 'Bu yıl sipariş oluşturan'}</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Aylık Müşteri Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>{t('dashboard.monthlyCustomerTrend', 'Aylık Müşteri Trendi')} ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyCustomerTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis allowDecimals={false} stroke="#b04309" />
                    <Tooltip formatter={(value) => [`${value}`, t('dashboard.customer', 'Müşteri')]} />
                    <Line type="monotone" dataKey="count" name={t('dashboard.customer', 'Müşteri')} stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Sipariş Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>{t('dashboard.monthlyOrderTrend', 'Aylık Sipariş Trendi')} ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyOrderTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis allowDecimals={false} stroke="#b04309" />
                    <Tooltip formatter={(value) => [`${value}`, t('dashboard.order', 'Sipariş')]} />
                    <Line type="monotone" dataKey="count" name={t('dashboard.order', 'Sipariş')} stroke="#b04309" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Aylık Kazanç Trendi */}
            <div className="stats-section">
              <div className="section-header">
                <h3>{t('dashboard.monthlyEarningsTrend', 'Aylık Kazanç Trendi')} ({new Date().getFullYear()})</h3>
              </div>
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={monthlyEarningsTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ed" />
                    <XAxis dataKey="month" stroke="#b04309" />
                    <YAxis
                      stroke="#b04309"
                      tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}K` : value}
                    />
                    <Tooltip
                      formatter={(value) => [`${formatPrice(value)} €`, t('dashboard.earnings', 'Kazanç')]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      name={t('dashboard.earnings', 'Kazanç')}
                      stroke="#2e7d32"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#2e7d32' }}
                      activeDot={{ r: 6, fill: '#1b5e20' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gelir İstatistikleri */}
            <div className="stats-section revenue-stats">
              <h3>{t('dashboard.revenueStats', 'Gelir İstatistikleri')}</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-coins"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.expectedRevenue', 'Beklenen Gelir')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.expectedTotal)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Totaal alle bestellingen' : 'Tüm siparişler toplamı'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-wallet"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.totalRevenue', 'Toplam Gelir')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.total)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Ontvangen betalingen' : 'Alınan ödemeler'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-hourglass-half"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.remainingBalance', 'Kalan Ödemeler')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.remainingTotal)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Nog te ontvangen' : 'Tahsil edilecek'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-day"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.today', 'Bugün')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.today)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Omzet vandaag' : 'Bugünkü gelir'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-week"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisWeek', 'Bu Hafta')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisWeek)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Laatste 7 dagen' : 'Son 7 gün'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisMonth', 'Bu Ay')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisMonth)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Omzet deze maand' : 'Bu ayki gelir'}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.thisYear', 'Bu Yıl')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.thisYear)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Omzet dit jaar' : 'Bu yılki gelir'}</small>
                  </div>
                </div>
              </div>

              {/* Ödeme Tipine Göre Günlük Gelirler */}
              <h4 className="payment-type-title"><i className="fas fa-receipt"></i> {language === 'nl' ? 'Naar Betaalmethode' : 'Ödeme Tipine Göre'}</h4>
              <div className="stats-grid payment-type-stats">
                <div className="stat-card payment-cash">
                  <div className="stat-icon">
                    <i className="fas fa-money-bill-wave"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{language === 'nl' ? 'Vandaag (Contant)' : 'Bugün (Nakit)'}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayCash)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Contante betalingen' : 'Elden nakit ödemeler'}</small>
                  </div>
                </div>
                <div className="stat-card payment-iban">
                  <div className="stat-icon">
                    <i className="fas fa-university"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{language === 'nl' ? 'Vandaag (IBAN)' : 'Bugün (IBAN)'}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayIban)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Bankoverschrijving' : 'Banka transferi'}</small>
                  </div>
                </div>
                <div className="stat-card payment-credit-card">
                  <div className="stat-icon">
                    <i className="fas fa-credit-card"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{language === 'nl' ? 'Vandaag (Pinpas)' : 'Bugün (Banka Kartı)'}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : `${formatPrice(revenueStats.todayBankCard || revenueStats.yesterdayCreditCard)} €`}
                    </div>
                    <small>{language === 'nl' ? 'Pin / Kaartbetalingen' : 'Banka kartı ile ödemeler'}</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Geçen Yıl Karşılaştırması */}
            <div className="stats-section">
              <h3>{t('dashboard.lastYearComparison', 'Geçen Yıl Karşılaştırması')} ({new Date().getFullYear() - 1})</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.lastYearToday', 'Geçen Yıl Bugün')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.today}
                    </div>
                    <small>{t('dashboard.sameDayCustomers', 'Aynı gün müşteri sayısı')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.lastYearThisWeek', 'Geçen Yıl Bu Hafta')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisWeek}
                    </div>
                    <small>{t('dashboard.sameWeekCustomers', 'Aynı hafta müşteri sayısı')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-history"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.lastYearThisMonth', 'Geçen Yıl Bu Ay')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.thisMonth}
                    </div>
                    <small>{t('dashboard.sameMonthCustomers', 'Aynı ay müşteri sayısı')}</small>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-chart-bar"></i>
                  </div>
                  <div className="stat-content">
                    <h3>{t('dashboard.lastYearTotal', 'Geçen Yıl Toplam')}</h3>
                    <div className="stat-number">
                      {isLoadingStats ? <div className="stat-spinner"></div> : lastYearStats.total}
                    </div>
                    <small>{t('dashboard.yearTotalCustomers', 'Yıl boyunca toplam')}</small>
                  </div>
                </div>
              </div>
            </div>

            {/* Backup/Restore Bölümü */}
            <div className="backup-section">
              <h3>{t('dashboard.dataManagement', 'Veri Yönetimi')}</h3>
              <div className="backup-grid">
                <div className="backup-card">
                  <div className="backup-icon">📥</div>
                  <h4>{t('dashboard.backupExport', 'Yedek Al')}</h4>
                  <p>{t('dashboard.backupExportDesc', 'Tüm müşteri ve sipariş verilerinizi JSON formatında yedekleyin')}</p>
                  <button
                    onClick={handleExport}
                    disabled={isExporting || isLoadingStats}
                    className="backup-btn export-btn"
                  >
                    {isExporting ? (
                      <span>
                        <div className="btn-spinner"></div>
                        {t('dashboard.exporting', 'Yedekleniyor...')}
                      </span>
                    ) : (
                      <>
                        <i className="fas fa-download"></i>
                        {t('dashboard.backupExport', 'Yedek Al')}
                      </>
                    )}
                  </button>
                </div>

                <div className="backup-card">
                  <div className="backup-icon">📤</div>
                  <h4>{t('dashboard.backupImport', 'Yedek Yükle')}</h4>
                  <p>{t('dashboard.backupImportDesc', 'Daha önce aldığınız backup dosyasını geri yükleyin')}</p>
                  <button
                    onClick={handleImportClick}
                    disabled={isImporting || isLoadingStats}
                    className="backup-btn import-btn"
                  >
                    <i className="fas fa-upload"></i>
                    {t('dashboard.backupImport', 'Yedek Yükle')}
                  </button>
                </div>
              </div>

              <div className="backup-info">
                <i className="fas fa-info-circle"></i>
                <p>
                  {t('dashboard.backupNotice', "Önemli: Yedek yükleme işlemi mevcut verilerinizi silmez. Aynı ID'ye sahip veriler güncellenir, yeni veriler eklenir.")}
                </p>
              </div>
            </div>

            {/* Firma Bilgileri Bölümü */}
            <div className="company-info-section">
              <div className="company-info-header">
                <h3>
                  <i className="fas fa-building"></i>
                  {t('dashboard.companyInfo', 'Firma Bilgileri')}
                </h3>
                <button
                  className="btn-toggle-company-info"
                  onClick={() => {
                    setShowCompanyInfo(!showCompanyInfo);
                    setCompanyError(null);
                    setCompanySuccess(false);
                    if (!showCompanyInfo) {
                      handleCancelCompany();
                    }
                  }}
                >
                  {showCompanyInfo ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      {t('dashboard.hide', 'Gizle')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      {t('dashboard.show', 'Göster')}
                    </>
                  )}
                </button>
              </div>

              {showCompanyInfo && (
                <div className="company-info-content">
                  {companyError && (
                    <div className="company-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {companyError}
                    </div>
                  )}

                  {companySuccess && (
                    <div className="company-message success">
                      <i className="fas fa-check-circle"></i>
                      {companySuccess}
                    </div>
                  )}

                  <form onSubmit={handleSaveCompany} className="company-form">
                    <div className="form-group">
                      <label htmlFor="companyName">
                        <i className="fas fa-building"></i>
                        {t('dashboard.companyName', 'Firma Adı *')}
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        value={companyForm.name}
                        onChange={(e) => setCompanyForm({ ...companyForm, name: e.target.value })}
                        placeholder={t('dashboard.companyNamePlaceholder', 'Firma adını girin')}
                        required
                        disabled={isLoadingCompanies}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="companyPhone">
                        <i className="fas fa-phone"></i>
                        {t('dashboard.companyPhone', 'Telefon Numarası *')}
                      </label>
                      <input
                        type="text"
                        id="companyPhone"
                        value={companyForm.phone}
                        onChange={(e) => setCompanyForm({ ...companyForm, phone: e.target.value })}
                        placeholder="905321234567 veya +905321234567"
                        required
                        disabled={isLoadingCompanies}
                      />
                      <small>{t('dashboard.companyPhoneHint', 'Ülke kodu ile birlikte girin (örn: 905321234567)')}</small>
                    </div>

                    <div className="company-form-actions">
                      <button
                        type="submit"
                        className="btn-save-company"
                        disabled={isLoadingCompanies}
                      >
                        {isLoadingCompanies ? (
                          <>
                            <div className="btn-spinner"></div>
                            {language === 'nl' ? 'Bezig met opslaan...' : 'Kaydediliyor...'}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {editingCompany ? (language === 'nl' ? 'Bijwerken' : 'Güncelle') : (language === 'nl' ? 'Toevoegen' : 'Ekle')}
                          </>
                        )}
                      </button>
                      {editingCompany && (
                        <button
                          type="button"
                          className="btn-cancel-company"
                          onClick={handleCancelCompany}
                          disabled={isLoadingCompanies}
                        >
                          {language === 'nl' ? 'Annuleren' : 'İptal'}
                        </button>
                      )}
                    </div>
                  </form>

                  {/* Firma Listesi */}
                  <div className="companies-list">
                    <h4>{t('dashboard.registeredCompanies', 'Kayıtlı Firmalar')}</h4>
                    {isLoadingCompanies ? (
                      <div className="loading-companies">
                        <div className="stat-spinner"></div>
                        <span>{language === 'nl' ? 'Laden...' : 'Yükleniyor...'}</span>
                      </div>
                    ) : companies.length === 0 ? (
                      <div className="no-companies">
                        <p>{t('dashboard.noCompanies', 'Henüz firma eklenmemiş.')}</p>
                      </div>
                    ) : (
                      <div className="companies-table-wrapper">
                        <table className="companies-table">
                          <thead>
                            <tr>
                              <th>{language === 'nl' ? 'Bedrijfsnaam' : 'Firma Adı'}</th>
                              <th>{language === 'nl' ? 'Telefoon' : 'Telefon'}</th>
                              <th>{language === 'nl' ? 'Acties' : 'İşlemler'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {companies.map((company) => (
                              <tr key={company.id}>
                                <td>{company.name}</td>
                                <td>{company.phone}</td>
                                <td>
                                  <div className="company-actions">
                                    <button
                                      onClick={() => handleEditCompany(company)}
                                      className="edit-company-btn"
                                      disabled={isLoadingCompanies}
                                      title={language === 'nl' ? 'Bewerken' : 'Düzenle'}
                                    >
                                      <i className="fas fa-edit"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteCompanyClick(company)}
                                      className="delete-company-btn"
                                      disabled={isLoadingCompanies}
                                      title={language === 'nl' ? 'Verwijderen' : 'Sil'}
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="company-info-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>{language === 'nl' ? 'Informatie:' : 'Bilgi:'}</strong>
                      <ul>
                        <li>{language === 'nl' ? 'Toegevoegde bedrijven worden getoond bij het verzenden van WhatsApp-berichten.' : 'Eklediğiniz firmalar WhatsApp mesaj gönderme bölümünde görünecektir.'}</li>
                        <li>{language === 'nl' ? 'Voer het telefoonnummer in met landcode (bijv: 31612345678 of 905321234567).' : 'Telefon numarasını ülke kodu ile birlikte girin (örn: 905321234567).'}</li>
                        <li>{language === 'nl' ? 'De bedrijfsnaam moet uniek zijn.' : 'Firma adı benzersiz olmalıdır.'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Şifre Değiştirme Bölümü */}
            <div className="password-change-section">
              <div className="password-change-header">
                <h3>
                  <i className="fas fa-key"></i>
                  {t('dashboard.changePassword', 'Şifre Değiştir')}
                </h3>
                <button
                  className="btn-toggle-password-change"
                  onClick={() => {
                    setShowPasswordChange(!showPasswordChange);
                    setPasswordError('');
                    setPasswordSuccess('');
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                >
                  {showPasswordChange ? (
                    <>
                      <i className="fas fa-chevron-up"></i>
                      {t('dashboard.hide', 'Gizle')}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down"></i>
                      {t('dashboard.show', 'Göster')}
                    </>
                  )}
                </button>
              </div>

              {showPasswordChange && (
                <div className="password-change-content">
                  {passwordError && (
                    <div className="password-message error">
                      <i className="fas fa-exclamation-circle"></i>
                      {passwordError}
                    </div>
                  )}

                  {passwordSuccess && (
                    <div className="password-message success">
                      <i className="fas fa-check-circle"></i>
                      {passwordSuccess}
                    </div>
                  )}

                  <form onSubmit={handleChangePassword} className="password-change-form">
                    <div className="form-group">
                      <label htmlFor="currentPassword">
                        <i className="fas fa-lock"></i>
                        {t('dashboard.currentPassword', 'Mevcut Şifre *')}
                      </label>
                      <input
                        type="password"
                        id="currentPassword"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder={t('dashboard.currentPasswordPlaceholder', 'Mevcut şifrenizi girin')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="current-password"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="newPassword">
                        <i className="fas fa-key"></i>
                        {t('dashboard.newPassword', 'Yeni Şifre *')}
                      </label>
                      <input
                        type="password"
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder={t('dashboard.newPasswordPlaceholder', 'Yeni şifrenizi girin (en az 6 karakter)')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                      <small>{t('dashboard.newPasswordHint', 'En az 6 karakter olmalıdır')}</small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="confirmPassword">
                        <i className="fas fa-key"></i>
                        {t('dashboard.confirmPassword', 'Yeni Şifre Tekrar *')}
                      </label>
                      <input
                        type="password"
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder={t('dashboard.confirmPasswordPlaceholder', 'Yeni şifrenizi tekrar girin')}
                        required
                        disabled={isChangingPassword}
                        autoComplete="new-password"
                        minLength={6}
                      />
                    </div>

                    <div className="password-change-actions">
                      <button
                        type="submit"
                        className="btn-change-password"
                        disabled={isChangingPassword}
                      >
                        {isChangingPassword ? (
                          <>
                            <div className="btn-spinner"></div>
                            {t('dashboard.changingPassword', 'Değiştiriliyor...')}
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            {language === 'nl' ? 'Wachtwoord Wijzigen' : 'Şifreyi Değiştir'}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel-password"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setPasswordError('');
                          setPasswordSuccess('');
                          setCurrentPassword('');
                          setNewPassword('');
                          setConfirmPassword('');
                        }}
                        disabled={isChangingPassword}
                      >
                        {language === 'nl' ? 'Annuleren' : 'İptal'}
                      </button>
                    </div>
                  </form>

                  <div className="password-change-info">
                    <i className="fas fa-info-circle"></i>
                    <div>
                      <strong>{language === 'nl' ? 'Beveiligingsinstructies:' : 'Güvenlik Notları:'}</strong>
                      <ul>
                        <li>{language === 'nl' ? 'Uw wachtwoord moet minimaal 6 tekens bevatten.' : 'Şifreniz en az 6 karakter olmalıdır.'}</li>
                        <li>{language === 'nl' ? 'Gebruik hoofdletters, kleine letters, cijfers en speciale tekens voor een sterk wachtwoord.' : 'Güvenli bir şifre için büyük/küçük harf, rakam ve özel karakter kullanın.'}</li>
                        <li>{language === 'nl' ? 'Na het wijzigen van uw wachtwoord blijft uw sessie actief.' : 'Şifre değiştirdikten sonra oturumunuz açık kalacaktır.'}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#fcfcfc' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-spinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto 15px' }}></div>
          <h2 style={{ color: '#555', fontSize: '18px', fontWeight: '500' }}>{t('header.checkPermissions', 'Yetkiler Kontrol Ediliyor...')}</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-top">
          <div className="header-brand-section">
            <h1>{t('header.title', 'Ebrar Gordijnen - Müşteri Listesi')}</h1>
          </div>
          <div className="header-actions">
            <LanguageSwitcher />
            {user?.email && (
              <span className="user-email">{user.email}</span>
            )}
            <button onClick={handleLogout} className="logout-button" title={t('common.logout', 'Çıkış Yap')}>
              <i className="fas fa-sign-out-alt"></i>
              <span>{t('common.logout', 'Çıkış Yap')}</span>
            </button>
          </div>
        </div>

        <div className="header-bottom">
          <nav className="dashboard-nav" aria-label="Ana Menü">
            {/* Ana Sayfa - Satışcı hariç herkes */}
            {permissions?.canAccessDashboard && (
              <button
                className={`nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActiveTab('dashboard')}
              >
                <i className="fas fa-home"></i>
                <span>{t('nav.home', 'Ana Sayfa')}</span>
              </button>
            )}

            {/* Planlama Sekmesi - Admin, Satışcı, Montajcı, Yazılımcı */}
            {permissions?.canAccessPlanning && (
              <button
                className={`nav-btn ${activeTab === 'planning' ? 'active' : ''}`}
                onClick={() => setActiveTab('planning')}
              >
                <i className="fas fa-route"></i>
                <span>{t('nav.planning', 'Planlama')}</span>
              </button>
            )}

            {/* Müşteri Listesi - Herkes (Montajcı sadece montaj durumundakiler) */}
            <button
              className={`nav-btn ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              <i className="fas fa-users"></i>
              <span>{isMontajci ? t('nav.installationList', 'Montaj Listesi') : t('nav.customers', 'Müşteri Listesi')}</span>
            </button>

            {/* Kullanıcı Yönetimi - Sadece Admin */}
            {isAdmin && (
              <button
                className={`nav-btn ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <i className="fas fa-users-cog"></i>
                <span>{t('nav.users', 'Kullanıcılar')}</span>
              </button>
            )}

            {/* Ürün Listesi - Montajcı hariç herkes */}
            {!isMontajci && (
              <button
                className={`nav-btn ${activeTab === 'products' ? 'active' : ''}`}
                onClick={() => setActiveTab('products')}
              >
                <i className="fas fa-tags"></i>
                <span>{t('nav.products', 'Ürün Listesi')}</span>
              </button>
            )}
          </nav>
        </div>
      </header>

      <main className="dashboard-main">
        {renderContent()}
      </main>

      <footer className="dashboard-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <i className="fas fa-store"></i>
            <span>{APP_NAME}</span>
          </div>

          <div className="footer-developer">
            <span>{t('common.developedBy', 'Developed by')}</span>
            <a href="https://kahramantech.com" target="_blank" rel="noopener noreferrer" className="developer-badge">
              <i className="fas fa-code"></i> KahramanTech
            </a>
          </div>

          <div className="footer-info">
            <span className="footer-copyright">
              © {new Date().getFullYear()} {t('common.allRightsReserved', 'Tüm hakları saklıdır.')}
            </span>
            <span className="version-badge">v{APP_VERSION}</span>
          </div>
        </div>
      </footer>

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal-content import-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-upload"></i>
                {language === 'nl' ? 'Back-upbestand Importeren' : 'Yedek Dosyası Yükle'}
              </h3>
              <button onClick={closeImportModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="file-upload-area">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  id="backup-file-input"
                  className="file-input"
                />
                <label htmlFor="backup-file-input" className="file-upload-label">
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span>{language === 'nl' ? 'Selecteer of sleep een JSON-bestand' : 'JSON dosyası seçin veya sürükleyin'}</span>
                </label>

                {importFile && (
                  <div className="selected-file">
                    <i className="fas fa-file-alt"></i>
                    <span>{importFile.name}</span>
                    <small>({(importFile.size / 1024).toFixed(1)} KB)</small>
                  </div>
                )}
              </div>

              <div className="import-warning">
                <i className="fas fa-exclamation-triangle"></i>
                <div>
                  <strong>{language === 'nl' ? 'Let op:' : 'Dikkat:'}</strong>
                  <ul>
                    <li>{language === 'nl' ? 'Het bestand moet in JSON-formaat zijn' : 'Backup dosyası JSON formatında olmalıdır'}</li>
                    <li>{language === 'nl' ? 'Bestaande gegevens blijven behouden, niet verwijderd' : 'Mevcut verileriniz korunur, silinmez'}</li>
                    <li>{language === 'nl' ? 'Gegevens met hetzelfde ID worden bijgewerkt' : 'Aynı ID\'ye sahip veriler güncellenir'}</li>
                    <li>{language === 'nl' ? 'Deze actie kan niet ongedaan worden gemaakt' : 'İşlem geri alınamaz, önceden yedek aldığınızdan emin olun'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleImport}
                disabled={!importFile || isImporting}
                className="btn-primary import-confirm-btn"
              >
                {isImporting ? (
                  <span>
                    <div className="btn-spinner"></div>
                    {language === 'nl' ? 'Bezig met importeren...' : 'Yükleniyor...'}
                  </span>
                ) : (
                  <>
                    <i className="fas fa-check"></i>
                    {language === 'nl' ? 'Back-up Importeren' : 'Yedek Yükle'}
                  </>
                )}
              </button>
              <button
                onClick={closeImportModal}
                disabled={isImporting}
                className="btn-secondary"
              >
                {language === 'nl' ? 'Annuleren' : 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {notificationModal.show && (
        <div className="modal-overlay">
          <div className={`modal-content notification-modal ${notificationModal.type}`}>
            <div className="modal-header">
              <h3>
                {notificationModal.type === 'success' && <i className="fas fa-check-circle"></i>}
                {notificationModal.type === 'error' && <i className="fas fa-times-circle"></i>}
                {notificationModal.type === 'warning' && <i className="fas fa-exclamation-triangle"></i>}
                {notificationModal.type === 'info' && <i className="fas fa-info-circle"></i>}
                {notificationModal.title}
              </h3>
              <button onClick={closeNotificationModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="notification-message">{notificationModal.message}</p>

              {/* Export detayları */}
              {notificationModal.details && notificationModal.details.customers !== undefined && !notificationModal.details.backupCustomers && (
                <div className="notification-details">
                  <div className="detail-item">
                    <i className="fas fa-users"></i>
                    <span>{notificationModal.details.customers} müşteri</span>
                  </div>
                  <div className="detail-item">
                    <i className="fas fa-shopping-cart"></i>
                    <span>{notificationModal.details.orders} sipariş</span>
                  </div>
                  {notificationModal.details.pieces !== undefined && (
                    <div className="detail-item">
                      <i className="fas fa-cubes"></i>
                      <span>{notificationModal.details.pieces} parça (adet)</span>
                    </div>
                  )}
                  {notificationModal.details.companies !== undefined && (
                    <div className="detail-item">
                      <i className="fas fa-building"></i>
                      <span>{notificationModal.details.companies} firma</span>
                    </div>
                  )}
                </div>
              )}

              {/* Import detayları */}
              {notificationModal.details && notificationModal.details.backupCustomers !== undefined && (
                <div className="notification-details import-details">
                  <div className="detail-section">
                    <h4>📊 Yedek Dosyası</h4>
                    <div className="detail-row">
                      <span>{notificationModal.details.backupCustomers} müşteri</span>
                      <span>{notificationModal.details.backupOrders} sipariş</span>
                      {notificationModal.details.backupCompanies !== undefined && (
                        <span>{notificationModal.details.backupCompanies} firma</span>
                      )}
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>✅ İşlem Sonucu</h4>
                    <div className="detail-grid">
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Müşteri</span>
                        <span className="detail-value">{notificationModal.details.addedCustomers}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Müşteri</span>
                        <span className="detail-value">{notificationModal.details.updatedCustomers}</span>
                      </div>
                      <div className="detail-item success">
                        <span className="detail-label">Yeni Sipariş</span>
                        <span className="detail-value">{notificationModal.details.addedOrders}</span>
                      </div>
                      <div className="detail-item info">
                        <span className="detail-label">Güncellenen Sipariş</span>
                        <span className="detail-value">{notificationModal.details.updatedOrders}</span>
                      </div>
                      {notificationModal.details.addedCompanies !== undefined && (
                        <div className="detail-item success">
                          <span className="detail-label">Yeni Firma</span>
                          <span className="detail-value">{notificationModal.details.addedCompanies}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={closeNotificationModal} className="btn-primary">
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firma Silme Onay Modal */}
      {deleteCompanyModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-company-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                {language === 'nl' ? 'Bedrijf Verwijderen' : 'Firma Sil'}
              </h3>
              <button onClick={handleCloseDeleteCompanyModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                {language === 'nl'
                  ? <>Weet u zeker dat u het bedrijf <strong>{deleteCompanyModal.name}</strong> wilt verwijderen?</>
                  : <><strong>{deleteCompanyModal.name}</strong> adlı firmayı silmek istediğinizden emin misiniz?</>}
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                {language === 'nl' ? 'Deze actie kan niet ongedaan worden gemaakt!' : 'Bu işlem geri alınamaz!'}
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleDeleteCompany}
                className="btn-primary delete-confirm-btn"
                disabled={isLoadingCompanies}
              >
                {isLoadingCompanies ? (
                  <>
                    <div className="btn-spinner"></div>
                    {language === 'nl' ? 'Verwijderen...' : 'Siliniyor...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    {language === 'nl' ? 'Ja, Verwijderen' : 'Evet, Sil'}
                  </>
                )}
              </button>
              <button
                onClick={handleCloseDeleteCompanyModal}
                className="btn-secondary"
                disabled={isLoadingCompanies}
              >
                {language === 'nl' ? 'Annuleren' : 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yeni Kullanıcı Ekleme Modal */}
      {showAddUserModal && (
        <div className="modal-overlay">
          <div className="modal-content add-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-user-plus"></i>
                {language === 'nl' ? 'Nieuwe Gebruiker Aanmaken' : 'Yeni Kullanıcı Oluştur'}
              </h3>
              <button onClick={() => {
                setShowAddUserModal(false);
                setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                setUserFormError(null);
              }} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleCreateUser}>
              <div className="modal-body">
                {userFormError && (
                  <div className="form-error">
                    <i className="fas fa-exclamation-circle"></i>
                    {userFormError}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="displayName">
                    <i className="fas fa-user"></i>
                    {t('userManagement.name', 'Kullanıcı Adı')}
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={newUserForm.displayName}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, displayName: e.target.value }))}
                    placeholder={language === 'nl' ? 'Bijv: Jan Jansen' : 'Örn: Ahmet Yılmaz'}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    {t('userManagement.email', 'E-posta')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="ornek@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i>
                    {language === 'nl' ? 'Wachtwoord' : 'Şifre'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={language === 'nl' ? 'Minimaal 6 tekens' : 'En az 6 karakter'}
                    minLength={6}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="role">
                    <i className="fas fa-user-tag"></i>
                    {t('userManagement.role', 'Rol')}
                  </label>
                  <select
                    id="role"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, role: e.target.value }))}
                  >
                    <option value={ROLES.SATISCI}>{t('userManagement.roles.satisci', 'Satışcı')}</option>
                    <option value={ROLES.MONTAJCI}>{t('userManagement.roles.montajci', 'Montajcı')}</option>
                    <option value={ROLES.YAZILIMCI}>{t('userManagement.roles.yazilimci', 'Yazılımcı')}</option>
                    <option value={ROLES.ADMIN}>{t('userManagement.roles.admin', 'Admin')}</option>
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-user-plus"></i>
                  {language === 'nl' ? 'Gebruiker Aanmaken' : 'Kullanıcı Oluştur'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setNewUserForm({ email: '', password: '', displayName: '', role: ROLES.SATISCI });
                    setUserFormError(null);
                  }}
                  className="btn-secondary"
                >
                  {language === 'nl' ? 'Annuleren' : 'İptal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kullanıcı Silme Onay Modal */}
      {deleteUserModal && (
        <div className="modal-overlay">
          <div className="modal-content delete-user-modal">
            <div className="modal-header">
              <h3>
                <i className="fas fa-exclamation-triangle"></i>
                {language === 'nl' ? 'Gebruiker Verwijderen' : 'Kullanıcı Sil'}
              </h3>
              <button onClick={() => setDeleteUserModal(null)} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p>
                {language === 'nl'
                  ? <>Weet u zeker dat u de gebruiker <strong>{deleteUserModal.displayName || deleteUserModal.email}</strong> wilt verwijderen?</>
                  : <><strong>{deleteUserModal.displayName || deleteUserModal.email}</strong> kullanıcısını silmek istediğinizden emin misiniz?</>}
              </p>
              <p className="warning-text">
                <i className="fas fa-exclamation-circle"></i>
                {language === 'nl'
                  ? 'Deze actie kan niet ongedaan worden gemaakt! De gebruiker verliest direct toegang tot het systeem.'
                  : 'Bu işlem geri alınamaz! Kullanıcı sisteme giriş yapamayacak.'}
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => handleDeleteUser(deleteUserModal.id)}
                className="btn-primary delete-confirm-btn"
              >
                <i className="fas fa-trash"></i>
                {language === 'nl' ? 'Ja, Verwijderen' : 'Evet, Sil'}
              </button>
              <button
                onClick={() => setDeleteUserModal(null)}
                className="btn-secondary"
              >
                {language === 'nl' ? 'Annuleren' : 'İptal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;